import type { Offer } from '../types.js';
import { hasRecordedPrice, PRICE_MAX_AGE_MS, recentlyChecked } from '../../../../packages/shared/src/buying.js';
import { affiliateDisclosure, clickTimeAffiliates, earnsCommission, type ClickTimeAffiliates } from '../../../../packages/shared/src/affiliate-disclosure.js';
import { OfferPromotion } from './OfferPromotion.js';
import { EditActions, EditLink, InlineEdit } from './EditLink.js';
import { useAdmin } from './EditLink.js';
import { buyingLinkBase, buyingLinkFields, buyingLinkTitle } from './BuyingLinks.js';
import { OfficialStoreBadge, VerificationBadge } from './VerificationBadge.js';

export function FragrancePrices({
  offers,
  country,
  countryName,
  affiliate,
  fragranceId,
  onSaved,
}: {
  offers: Offer[];
  country: string;
  countryName?: string;
  // Required: without the server's click-time settings a tagged link would render with no disclosure.
  affiliate: boolean | ClickTimeAffiliates;
  fragranceId?: string;
  onSaved?: () => void;
}) {
  const { admin } = useAdmin();
  const clickTime = clickTimeAffiliates(affiliate);
  const local = country
    ? offers.filter((o) => o.country === country && o.deliveryStatus !== 'UNSUPPORTED')
    : [];
  if (!local.length && !admin) return null;
  return (
    <section className="fragrance-prices" aria-label="Fragrance prices">
      <h2>{countryName ? 'Prices in ' + countryName : 'Price'}</h2>
      {!local.length && (
        <p className="price-note">
          {country
            ? 'No buying links for this country yet.'
            : 'Choose your country to see prices.'}
        </p>
      )}
      {local.map((o) => {
        const title = buyingLinkTitle(o);
        const priced = !o.amazon && hasRecordedPrice(o);
        return (
          <div className="fragrance-price" key={o.id}>
            <div className="fragrance-price-badges">
              <VerificationBadge status={o.verificationStatus} />
              {o.officialStore && <OfficialStoreBadge />}
            </div>
            <div className="fragrance-price-line">
              <strong>
                {priced
                  ? new Intl.NumberFormat('en-GB', {
                      style: 'currency',
                      currency: o.currency!,
                    }).format(Number(o.price))
                  : 'Check price'}
                {priced && <small> {o.currency}</small>}
              </strong>
              <span>{o.bottleSizeMl ? o.bottleSizeMl + ' ml' : 'Check bottle size'}</span>
            </div>
            <a
              className="buying-button"
              href={'/go/offer/' + o.id + '?country=' + encodeURIComponent(country)}
              target="_blank"
              rel="sponsored nofollow noopener noreferrer"
            >
              {title}
            </a>
            {/* The disclosure sits directly under the link it describes, where a reader decides to click. */}
            {earnsCommission(o.url, clickTime, o.affiliate) && (
              <p className="affiliate-note below-button">{affiliateDisclosure}</p>
            )}
            <OfferPromotion offer={o} />
            <EditActions>
              <InlineEdit entity="offers" id={o.id} label="Edit buying link" srLabel={'Edit ' + title + ' buying link'} fields={buyingLinkFields(country)} onSaved={onSaved} />
            </EditActions>
            {priced && o.priceCheckedAt && (
              <p className="price-note">
                Checked{' '}
                <time dateTime={o.priceCheckedAt}>
                  {new Date(o.priceCheckedAt).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    timeZone: 'UTC',
                  })}
                </time>
                {recentlyChecked(o.priceCheckedAt, PRICE_MAX_AGE_MS) && o.availability === 'OUT_OF_STOCK' ? ' · Out of stock when checked' : ''}
              </p>
            )}
          </div>
        );
      })}
      {local.length > 0 && (
        <p className="price-note">
          Retailers confirm the final price and delivery at checkout.
        </p>
      )}
      {fragranceId && <EditActions>
        <InlineEdit
          entity="offers"
          icon="plus"
          label="Add a buying link"
          base={buyingLinkBase(fragranceId, country)}
          fields={buyingLinkFields(country)}
          onSaved={onSaved}
        />
        <EditLink label="Manage all buying links" to={'/admin/offers?fragranceId=' + encodeURIComponent(fragranceId)} />
      </EditActions>}
    </section>
  );
}
